import React, { useEffect, useRef, useState } from 'react';
import { trialPublicService } from '../../services/trialApi';
import logo from '../../assets/jeta-logo.png';
import './TrialRequest.css';

// Public, no-login RollShopXpert trial request page — reachable at /trial-request.
// Four steps: details -> terms -> verify email -> confirmation.

const FREE = ['gmail.com','googlemail.com','yahoo.com','yahoo.co.in','yahoo.in','hotmail.com','outlook.com','live.com','msn.com','rediffmail.com','icloud.com','me.com','aol.com','proton.me','protonmail.com','zoho.com','yandex.com','gmx.com','mail.com'];
// Single plain address only (no commas, quotes, brackets); mirrors the server-side check.
const EMAIL_RE = /^[A-Za-z0-9._%+'-]+@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;
const PLANS = ['Silver', 'Gold', 'Platinum'];
const CONSENTS = ['accepted', 'authorised', 'dataNotBackedUp', 'customerReference'];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const nextMonday = () => { const d = new Date(); d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7)); return ymd(d); };

const emptyForm = () => ({
  company: '', address: '', gstin: '', site: '', contactName: '', designation: '', email: '', phone: '',
  users: 3, preferredStart: nextMonday(), plan: 'Gold', goals: '',
});
const noConsents = () => ({ accepted: false, authorised: false, dataNotBackedUp: false, customerReference: false });

function emailProblem(raw) {
  const v = raw.trim().toLowerCase();
  if (!v) return '';
  if (v.length > 254 || !EMAIL_RE.test(v)) return 'Enter a valid email address, like name@yourcompany.com.';
  const dom = v.split('@')[1];
  if (FREE.includes(dom)) return `${dom} is a personal email provider. Use your company email so we can verify your organisation.`;
  return '';
}

const fmtIst = (d) => new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }) + ' IST';
const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
const apiMsg = (err, fallback) => err.response?.data?.message || fallback;

function KV({ rows }) {
  return (
    <dl className="kv">
      {rows.map(([k, v, mono]) => (
        <React.Fragment key={k}>
          <dt>{k}</dt>
          <dd className={mono ? 'mono' : undefined}>{v}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

export default function TrialRequest() {
  const [step, setStep] = useState(1);
  const [agreement, setAgreement] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [invalid, setInvalid] = useState({});
  const [s1Err, setS1Err] = useState('');
  const [consents, setConsents] = useState(noConsents);
  const [readAll, setReadAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [s2Err, setS2Err] = useState('');
  const [requestId, setRequestId] = useState(null);
  const [otp, setOtp] = useState('');
  const [otpErr, setOtpErr] = useState('');
  const [resent, setResent] = useState(false);
  const [record, setRecord] = useState(null);
  const termsRef = useRef(null);

  const emailErr = emailProblem(form.email);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    trialPublicService.getTerms()
      .then(res => setAgreement(res.data))
      .catch(() => setLoadErr('We could not load the trial agreement. Please refresh the page.'));
  }, []);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [step]);

  const checkRead = () => {
    const el = termsRef.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 12) setReadAll(true);
  };
  useEffect(() => { if (step === 2) setTimeout(checkRead, 50); }, [step, agreement]);

  const submitDetails = (e) => {
    e.preventDefault();
    const req = ['company', 'address', 'site', 'contactName', 'designation', 'email', 'phone', 'users', 'preferredStart'];
    const labels = { company: 'Registered company name', address: 'Registered address', site: 'Plant / site location', contactName: 'Full name', designation: 'Designation', email: 'Official email', phone: 'Mobile', users: 'Number of users', preferredStart: 'Preferred start date' };
    const bad = req.filter(k => !String(form[k]).trim());
    setInvalid(Object.fromEntries(bad.map(k => [k, true])));
    const users = Number(form.users);
    let msg = '';
    if (bad.length) msg = 'Fill in: ' + bad.map(k => labels[k]).join(', ') + '.';
    else if (emailErr) msg = 'Check your official email address.';
    else if (!Number.isInteger(users) || users < 1 || users > 10) msg = 'Number of users must be between 1 and 10.';
    setS1Err(msg);
    if (!msg) setStep(2);
  };

  const submitTerms = async (e) => {
    e.preventDefault();
    if (!CONSENTS.every(c => consents[c]) || busy) return;
    setBusy(true); setS2Err('');
    try {
      const res = await trialPublicService.submit({
        ...form, users: Number(form.users), consents,
        agreementVersion: agreement.version, agreementHash: agreement.hash,
      });
      setRequestId(res.data.id);
      setOtp(''); setOtpErr(''); setResent(false);
      setStep(3);
    } catch (err) {
      setS2Err(apiMsg(err, 'Something went wrong. Please try again.'));
    } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setOtpErr('');
    try {
      const res = await trialPublicService.verify(requestId, otp.trim());
      setRecord(res.data.record);
      setStep(4);
    } catch (err) {
      setOtpErr(apiMsg(err, 'Could not verify the code. Please try again.'));
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setOtpErr(''); setResent(false);
    try { await trialPublicService.resend(requestId); setResent(true); }
    catch (err) { setOtpErr(apiMsg(err, 'Could not resend the code.')); }
  };

  const restart = () => {
    setForm(emptyForm()); setConsents(noConsents()); setReadAll(false); setInvalid({});
    setS1Err(''); setS2Err(''); setRequestId(null); setRecord(null); setStep(1);
  };

  const co = form.company.trim();
  const steps = ['Your details', 'Trial terms', 'Verify email', 'Confirmation'];
  const inv = (k) => (invalid[k] ? 'invalid' : undefined);

  return (
    <div className="trial-page">
      <header className="band"><div className="wrap">
        <div className="brand-row">
          <img src={logo} alt="JetaTech – Solutions for Business" className="brand-logo" />
          <div className="brand">RollShopXpert</div>
        </div>
        <h1>Request a RollShopXpert trial</h1>
        <p>Tell us about your roll shop, accept the trial terms, and confirm your work email. We email you a PDF copy of the agreement for your records.</p>
        <ol className="steps">
          {steps.map((label, i) => (
            <li key={label} className={step === i + 1 ? 'on' : step > i + 1 ? 'done' : ''}><span>{i + 1}</span>{label}</li>
          ))}
        </ol>
      </div></header>

      <main><div className="wrap">
        {loadErr && <div className="panel"><div className="err">{loadErr}</div></div>}

        {/* STEP 1 */}
        {step === 1 && (
          <form className="panel" noValidate onSubmit={submitDetails}>
            <h2>Your details</h2>
            <fieldset>
              <legend>Organisation</legend>
              <div className="grid">
                <label className="f full"><span>Registered company name</span><input type="text" className={inv('company')} value={form.company} onChange={set('company')} placeholder="e.g. Shree Ganesh Rolling Mills Pvt. Ltd." /></label>
                <label className="f full"><span>Registered address</span><textarea className={inv('address')} value={form.address} onChange={set('address')} placeholder="Plot, street, city, state, PIN" /></label>
                <label className="f"><span>GSTIN <i>(optional)</i></span><input type="text" value={form.gstin} onChange={set('gstin')} placeholder="27AAAAA0000A1Z5" maxLength={15} /></label>
                <label className="f"><span>Plant / site location</span><input type="text" className={inv('site')} value={form.site} onChange={set('site')} placeholder="e.g. Jalna, Maharashtra" /></label>
              </div>
            </fieldset>
            <fieldset>
              <legend>Person accepting the terms</legend>
              <div className="grid">
                <label className="f"><span>Full name</span><input type="text" className={inv('contactName')} value={form.contactName} onChange={set('contactName')} placeholder="e.g. Priya Kulkarni" /></label>
                <label className="f"><span>Designation</span><input type="text" className={inv('designation')} value={form.designation} onChange={set('designation')} placeholder="e.g. Head – Roll Shop" /></label>
                <label className="f"><span>Official email</span>
                  <input type="email" className={emailErr || invalid.email ? 'invalid' : undefined} value={form.email} onChange={set('email')} placeholder="name@yourcompany.com" />
                  <span className={emailErr ? 'err' : 'hint'}>{emailErr || 'Use your company email. Personal addresses (Gmail, Yahoo and similar) are not accepted.'}</span>
                </label>
                <label className="f"><span>Mobile</span><input type="tel" className={inv('phone')} value={form.phone} onChange={set('phone')} placeholder="+91 98xxx xxxxx" /></label>
              </div>
            </fieldset>
            <fieldset>
              <legend>Trial preferences</legend>
              <div className="grid">
                <label className="f"><span>Number of users</span><input type="number" className={inv('users')} min="1" max="10" value={form.users} onChange={set('users')} /><span className="hint">Up to 10 for a trial.</span></label>
                <label className="f"><span>Preferred start date</span><input type="date" className={inv('preferredStart')} min={ymd(new Date())} value={form.preferredStart} onChange={set('preferredStart')} /><span className="hint">JetaTech confirms the start and end dates on approval.</span></label>
                <div className="full" style={{ display: 'grid', gap: 8 }}>
                  <span style={{ fontSize: '13.5px', fontWeight: 500 }}>Subscription type for demo</span>
                  <div className="chips" role="radiogroup" aria-label="Subscription type for demo">
                    {PLANS.map(p => (
                      <label key={p} className="chip"><input type="radio" name="plan" value={p} checked={form.plan === p} onChange={set('plan')} />{p}</label>
                    ))}
                  </div>
                  <span className="hint">The demo runs with the features of the subscription you choose.</span>
                </div>
                <label className="f full"><span>What do you want to evaluate? <i>(optional)</i></span><textarea value={form.goals} onChange={set('goals')} placeholder="e.g. Track diameter loss per campaign across two mills" /></label>
              </div>
            </fieldset>
            {s1Err && <div className="err">{s1Err}</div>}
            <div className="actions"><span className="hint">All fields except optional ones are required.</span><button className="primary" type="submit" disabled={!agreement}>Continue to terms</button></div>
          </form>
        )}

        {/* STEP 2 */}
        {step === 2 && agreement && (
          <form className="panel" noValidate onSubmit={submitTerms}>
            <h2>Trial Access Agreement</h2>
            <div className="meta"><span>Version <code>{agreement.version}</code></span><span>Dated <code>{agreement.dated}</code></span><span>Fingerprint (SHA-256) <code>{agreement.hash.slice(0, 16)}…</code></span></div>
            <div className="terms" ref={termsRef} onScroll={checkRead} tabIndex={0} aria-label="Trial Access Agreement text">
              {agreement.terms.map(t => (<React.Fragment key={t.heading}><h3>{t.heading}</h3><p>{t.body}</p></React.Fragment>))}
            </div>
            <div className={`read-flag${readAll ? ' ok' : ''}`}>{readAll ? 'You have reached the end of the agreement.' : 'Scroll to the end of the agreement to enable acceptance.'}</div>

            {[
              ['accepted', <>I have read and accept the RollShopXpert Trial Access Agreement (v{agreement.version}) on behalf of <strong>{co}</strong>.</>],
              ['authorised', <>I confirm I am authorised to accept this agreement on behalf of <strong>{co}</strong>.</>],
              ['dataNotBackedUp', <>I understand trial data is not backed up, and we will not enter confidential or personal data during the trial.</>],
              ['customerReference', <>I agree that JetaTech may list <strong>{co}</strong> in its customer portfolio during and after the trial.<small>Section 8. Any case study, testimonial or quote needs your separate written approval.</small></>],
            ].map(([k, label]) => (
              <label className="check" key={k}>
                <input type="checkbox" disabled={!readAll} checked={consents[k]} onChange={(e) => setConsents(c => ({ ...c, [k]: e.target.checked }))} />
                <span>{label}</span>
              </label>
            ))}

            {s2Err && <div className="err">{s2Err}</div>}
            <div className="actions">
              <button type="button" className="ghost" onClick={() => setStep(1)}>Back</button>
              <button className="primary" type="submit" disabled={busy || !CONSENTS.every(c => consents[c])}>{busy ? 'Sending code…' : 'Accept and send verification code'}</button>
            </div>
          </form>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <form className="panel" noValidate onSubmit={verify}>
            <h2>Confirm your work email</h2>
            <p style={{ margin: 0 }}>We sent a 6-digit code to <strong>{form.email.trim()}</strong>. Your acceptance takes effect once you enter it. The code is valid for 10 minutes.</p>
            <label className="f"><span>Verification code</span>
              <input type="text" className={`otp${otpErr ? ' invalid' : ''}`} inputMode="numeric" maxLength={6} autoComplete="one-time-code" placeholder="••••••" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} />
            </label>
            {otpErr && <div className="err">{otpErr}</div>}
            {resent && <div className="hint">A new code has been sent.</div>}
            <div className="hint">Didn't get it? Check spam, or <button type="button" className="link" onClick={resend}>send a new code</button>.</div>
            <div className="actions">
              <button type="button" className="ghost" onClick={() => setStep(1)}>Change details</button>
              <button className="primary" type="submit" disabled={busy || otp.length !== 6}>{busy ? 'Verifying…' : 'Verify and accept'}</button>
            </div>
          </form>
        )}

        {/* STEP 4 */}
        {step === 4 && record && (
          <section className="panel">
            <h2>Trial request received</h2>
            <p className="notice">A PDF of the agreement with this acceptance record has been emailed to <strong>{record.email}</strong>, with a copy to JetaTech. Our team will confirm your trial dates and send login details within 1 business day.</p>
            <div className="record" aria-label="Acceptance record">
              <div className="rec-head"><strong>Acceptance Record</strong><span>{record.reference}</span></div>
              <div className="sect">Customer (Schedule A)</div>
              <KV rows={[
                ['Company', record.company],
                ['Registered address', record.address],
                ['GSTIN', record.gstin || 'Not provided'],
                ['Plant / site', record.site],
                ['Contact', `${record.contactName}, ${record.designation}`],
                ['Official email', record.email],
                ['Mobile', record.phone],
              ]} />
              <div className="sect">Trial</div>
              <KV rows={[
                ['Product', `${record.product} — Trial version`],
                ['Authorized users', record.users],
                ['Subscription type (demo)', record.plan],
                ['Preferred start', fmtDate(record.preferredStart)],
                ['Trial dates', 'To be confirmed by JetaTech on approval'],
                ['Evaluation goals', record.goals || 'Not provided'],
              ]} />
              <div className="sect">Electronic acceptance (Section 12)</div>
              <KV rows={[
                ['Accepted by', `${record.contactName} (${record.designation})`],
                ['Terms accepted', 'Yes, including authority to bind the company and the trial data conditions'],
                ['Customer reference consent', record.customerReferenceConsent ? 'Given (Section 8)' : 'Not given'],
                ['Agreement version', `v${record.agreementVersion} (${record.agreementDated})`],
                ['Terms fingerprint', record.agreementHash, true],
                ['Terms accepted at', fmtIst(record.acceptedAt), true],
                ['Email verified at', fmtIst(record.emailVerifiedAt), true],
                ['IP address', record.ip || 'Recorded by server', true],
                ['Reference', record.reference, true],
              ]} />
            </div>
            <div className="actions"><span className="hint">The full agreement text follows this record in the PDF.</span><button type="button" className="ghost" onClick={restart}>Start a new request</button></div>
          </section>
        )}

        <footer style={{ marginTop: 20 }}>JetaTech · Trial requests are reviewed before access is granted. Questions: <span style={{ userSelect: 'all' }}>subscriptions@jetatech.in</span></footer>
      </div></main>
    </div>
  );
}
